import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dropdown,
  Field,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Option,
  Text,
  ToggleButton,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { ChevronDown12Regular, Dismiss16Regular } from "@fluentui/react-icons";
import { CELLS_PER_DAY, CELL_MIN, DAYS, DAY_SHORT, GRID_START_MIN, cellsToSlots, cloneCells, runningSummary, toHHMM, toMin, type Cells, type Weekday } from "@gymplan/shared";

/** Per-cell decoration drawn on top of the grid (read-only context). */
export type CellMark = "pref" | "added" | "removed";

const CELL_PX = 18;
const HINT_KEY = "rackup.gridHintSeen";
const TIMES = Array.from({ length: CELLS_PER_DAY + 1 }, (_, i) => toHHMM(GRID_START_MIN + i * CELL_MIN));
const WEEKDAYS: Weekday[] = ["MON", "TUE", "WED", "THU", "FRI"];

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: "12px", minWidth: 0 },
  frame: { position: "relative", overflowX: "auto", border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusLarge, backgroundColor: tokens.colorNeutralBackground1 },
  grid: { display: "grid", gridTemplateColumns: "44px repeat(7, minmax(38px, 1fr))", minWidth: "320px", userSelect: "none", outlineStyle: "none" },
  head: { position: "sticky", top: 0, zIndex: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: "2px", padding: "6px 2px", backgroundColor: tokens.colorNeutralBackground1, borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  times: { position: "relative", borderRight: `1px solid ${tokens.colorNeutralStroke2}`, touchAction: "pan-y" },
  time: { position: "absolute", right: "6px", transform: "translateY(-50%)", fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3 },
  col: { display: "flex", flexDirection: "column", borderRight: `1px solid ${tokens.colorNeutralStroke3}`, touchAction: "none" },
  cell: { height: `${CELL_PX}px`, boxSizing: "border-box", borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, cursor: "pointer", position: "relative" },
  hourLine: { borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  // Translucent so the hour lines and times still read through the blocks.
  on: { backgroundColor: `color-mix(in srgb, ${tokens.colorBrandBackground} 38%, transparent)`, boxShadow: `inset 3px 0 0 ${tokens.colorBrandBackground}` },
  focus: { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px", zIndex: 1 },
  ghost: { backgroundImage: `repeating-linear-gradient(135deg, ${tokens.colorBrandBackground2} 0 6px, transparent 6px 10px)` },
  readOnly: { cursor: "default" },
  markPref: { boxShadow: `inset 3px 0 0 ${tokens.colorPaletteMarigoldBorderActive}` },
  markAdded: { boxShadow: `inset 0 0 0 2px ${tokens.colorPaletteGreenBorderActive}` },
  markRemoved: { backgroundColor: tokens.colorPaletteRedBackground2, boxShadow: `inset 0 0 0 2px ${tokens.colorPaletteRedBorderActive}` },
  empty: { position: "absolute", inset: "48px 0 0 52px", display: "grid", placeItems: "center", pointerEvents: "none" },
  emptyCard: { padding: "10px 14px", borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground1, boxShadow: tokens.shadow8, textAlign: "center", maxWidth: "260px" },
  quick: { display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "8px", padding: "12px", borderRadius: tokens.borderRadiusLarge, backgroundColor: tokens.colorNeutralBackground2 },
  dayToggles: { display: "flex", flexWrap: "wrap", gap: "4px" },
  footer: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px" },
  summary: { flex: 1, minWidth: "200px" },
});

export interface AvailabilityGridProps {
  value: Cells;
  onChange?: (next: Cells) => void;
  readOnly?: boolean;
  marks?: Partial<Record<Weekday, (CellMark | null)[]>>;
  /** Shown under the grid next to the summary (Save / Next) */
  actions?: React.ReactNode;
  /** Hide the quick-start panel even when empty */
  noQuickStart?: boolean;
}

/**
 * Week grid of half-hour cells (05:00–22:00). Tap a cell to toggle it, or drag to paint several.
 * Arrow keys move, Space toggles. Never shown as a blank canvas without help: an empty grid gets a
 * prompt, a ghost example and a quick-start (pick days → hours → fill).
 */
export function AvailabilityGrid({ value, onChange, readOnly = false, marks, actions, noQuickStart = false }: AvailabilityGridProps) {
  const s = useStyles();
  const [paint, setPaint] = useState<boolean | null>(null);
  const [touched, setTouched] = useState(false);
  const [focus, setFocus] = useState<{ d: number; i: number } | null>(null);
  const [hintSeen, setHintSeen] = useState(() => {
    try {
      return localStorage.getItem(HINT_KEY) === "1";
    } catch {
      return false;
    }
  });
  const latest = useRef(value);
  latest.current = value;

  const isEmpty = DAYS.every((d) => value[d].every((c) => !c));
  const editable = !readOnly && !!onChange;

  const set = useCallback(
    (d: Weekday, i: number, on: boolean) => {
      if (latest.current[d][i] === on) return;
      const next = cloneCells(latest.current);
      next[d][i] = on;
      latest.current = next;
      onChange?.(next);
    },
    [onChange],
  );

  const cellAt = (x: number, y: number): { d: Weekday; i: number } | null => {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const d = el?.dataset.day as Weekday | undefined;
    const i = el?.dataset.i;
    return d && i !== undefined ? { d, i: Number(i) } : null;
  };

  const dismissHint = () => {
    setHintSeen(true);
    try {
      localStorage.setItem(HINT_KEY, "1");
    } catch {
      /* private mode */
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!editable || e.button !== 0) return;
    const c = cellAt(e.clientX, e.clientY);
    if (!c) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const on = !latest.current[c.d][c.i];
    setPaint(on);
    setTouched(true);
    set(c.d, c.i, on);
    setFocus({ d: DAYS.indexOf(c.d), i: c.i });
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (paint === null) return;
    const c = cellAt(e.clientX, e.clientY);
    if (c) set(c.d, c.i, paint);
  };
  const endPaint = () => {
    if (paint !== null && !hintSeen) dismissHint();
    setPaint(null);
  };
  useEffect(() => {
    window.addEventListener("pointerup", endPaint);
    return () => window.removeEventListener("pointerup", endPaint);
  });

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!editable) return;
    const f = focus ?? { d: 0, i: 2 };
    const move = (dd: number, di: number) => {
      e.preventDefault();
      setFocus({ d: Math.max(0, Math.min(6, f.d + dd)), i: Math.max(0, Math.min(CELLS_PER_DAY - 1, f.i + di)) });
    };
    if (e.key === "ArrowUp") move(0, -1);
    else if (e.key === "ArrowDown") move(0, 1);
    else if (e.key === "ArrowLeft") move(-1, 0);
    else if (e.key === "ArrowRight") move(1, 0);
    else if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      const d = DAYS[f.d]!;
      setTouched(true);
      set(d, f.i, !latest.current[d][f.i]);
      setFocus(f);
    }
  };

  // ---- whole-day helpers ----
  const copyDay = (from: Weekday, to: Weekday[]) => {
    const next = cloneCells(latest.current);
    for (const d of to) next[d] = [...next[from]];
    onChange?.(next);
    setTouched(true);
  };
  const clearDay = (d: Weekday) => {
    const next = cloneCells(latest.current);
    next[d] = Array<boolean>(CELLS_PER_DAY).fill(false);
    onChange?.(next);
  };

  const slots = cellsToSlots(value);
  const mondayHasHours = value.MON.some(Boolean);
  const showGhost = editable && isEmpty && !touched;

  return (
    <div className={s.root}>
      {editable && !hintSeen && (
        <MessageBar intent="info">
          <MessageBarBody>Tap a block to switch it on or off — or drag across the hours to paint several at once.</MessageBarBody>
          <MessageBarActions containerAction={<Button appearance="transparent" aria-label="Dismiss" icon={<Dismiss16Regular />} onClick={dismissHint} />} />
        </MessageBar>
      )}

      {editable && isEmpty && !noQuickStart && <QuickStart onFill={(days, a, b) => {
        const next = cloneCells(latest.current);
        for (const d of days) for (let i = 0; i < CELLS_PER_DAY; i++) {
          const cs = GRID_START_MIN + i * CELL_MIN;
          if (cs >= toMin(a) && cs + CELL_MIN <= toMin(b)) next[d][i] = true;
        }
        setTouched(true);
        onChange?.(next);
      }} />}

      <div className={s.frame}>
        <div
          className={s.grid}
          role="grid"
          aria-label="Weekly availability. Arrow keys move, Space switches an hour on or off."
          aria-readonly={!editable}
          tabIndex={editable ? 0 : -1}
          onKeyDown={onKeyDown}
          onFocus={() => editable && !focus && setFocus({ d: 0, i: 2 })}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
        >
          <div className={s.head} />
          {DAYS.map((d) => (
            <div key={d} className={s.head} role="columnheader">
              <Text size={200} weight="semibold">{DAY_SHORT[d]}</Text>
              {editable && (
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <Button size="small" appearance="transparent" icon={<ChevronDown12Regular />} aria-label={`${DAY_SHORT[d]} options`} style={{ minWidth: 0, padding: 0 }} />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      {d !== "MON" && <MenuItem disabled={!mondayHasHours} onClick={() => copyDay("MON", [d])}>Same as Monday</MenuItem>}
                      {WEEKDAYS.includes(d) && <MenuItem disabled={!value[d].some(Boolean)} onClick={() => copyDay(d, WEEKDAYS.filter((x) => x !== d))}>Apply {DAY_SHORT[d]} to weekdays</MenuItem>}
                      <MenuItem disabled={!value[d].some(Boolean)} onClick={() => copyDay(d, DAYS.filter((x) => x !== d))}>Apply {DAY_SHORT[d]} to every day</MenuItem>
                      <MenuItem disabled={!value[d].some(Boolean)} onClick={() => clearDay(d)}>Clear {DAY_SHORT[d]}</MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              )}
            </div>
          ))}

          <div className={s.times} style={{ height: CELLS_PER_DAY * CELL_PX }} aria-hidden>
            {TIMES.map((t, i) => (i > 0 && i % 2 === 0 && i < CELLS_PER_DAY ? <span key={t} className={s.time} style={{ top: i * CELL_PX }}>{t}</span> : null))}
          </div>
          {DAYS.map((d, di) => (
            <div key={d} className={s.col} role="row">
              {value[d].map((on, i) => {
                const mark = marks?.[d]?.[i] ?? null;
                const ghost = showGhost && d === "MON" && i >= 4 && i < 8; // 07:00–09:00 example
                return (
                  <div
                    key={i}
                    data-day={d}
                    data-i={i}
                    role="gridcell"
                    aria-selected={on}
                    aria-label={`${DAY_SHORT[d]} ${TIMES[i]}–${TIMES[i + 1]}${on ? ", available" : ""}`}
                    className={mergeClasses(
                      s.cell,
                      i % 2 === 1 && s.hourLine,
                      on && s.on,
                      ghost && !on && s.ghost,
                      !editable && s.readOnly,
                      mark === "pref" && s.markPref,
                      mark === "added" && s.markAdded,
                      mark === "removed" && s.markRemoved,
                      !!focus && focus.d === di && focus.i === i && s.focus,
                    )}
                  />
                );
              })}
            </div>
          ))}
        </div>
        {showGhost && (
          <div className={s.empty}>
            <div className={s.emptyCard}>
              <Text weight="semibold" block>Tap the hours you can train clients</Text>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Like the striped example on Monday — or use the quick start above.</Text>
            </div>
          </div>
        )}
      </div>

      <div className={s.footer}>
        <Text className={s.summary} weight="semibold" aria-live="polite">{runningSummary(slots)}</Text>
        {editable && mondayHasHours && WEEKDAYS.some((d) => value[d].join() !== value.MON.join()) && (
          <Button size="small" appearance="secondary" onClick={() => copyDay("MON", ["TUE", "WED", "THU", "FRI"])}>Apply Monday to weekdays</Button>
        )}
        {actions}
      </div>
    </div>
  );
}

const QUICK_TIMES = TIMES.filter((_, i) => i % 2 === 0);

function QuickStart({ onFill }: { onFill: (days: Weekday[], start: string, end: string) => void }) {
  const s = useStyles();
  const [days, setDays] = useState<Weekday[]>(WEEKDAYS);
  const [a, setA] = useState("06:00");
  const [b, setB] = useState("12:00");
  const bad = toMin(b) <= toMin(a) || !days.length;
  return (
    <div className={s.quick}>
      <Field label="Quick start — your days" size="small">
        <div className={s.dayToggles}>
          {DAYS.map((d) => (
            <ToggleButton key={d} size="small" appearance={days.includes(d) ? "primary" : "secondary"} checked={days.includes(d)} onClick={() => setDays((x) => (x.includes(d) ? x.filter((y) => y !== d) : DAYS.filter((y) => x.includes(y) || y === d)))}>
              {DAY_SHORT[d]}
            </ToggleButton>
          ))}
        </div>
      </Field>
      <Field label="From" size="small">
        <Dropdown size="small" value={a} selectedOptions={[a]} onOptionSelect={(_, d) => setA(d.optionValue!)} style={{ minWidth: 90 }}>
          {QUICK_TIMES.map((t) => <Option key={t} value={t}>{t}</Option>)}
        </Dropdown>
      </Field>
      <Field label="To" size="small">
        <Dropdown size="small" value={b} selectedOptions={[b]} onOptionSelect={(_, d) => setB(d.optionValue!)} style={{ minWidth: 90 }}>
          {QUICK_TIMES.map((t) => <Option key={t} value={t}>{t}</Option>)}
        </Dropdown>
      </Field>
      <Button appearance="primary" size="small" disabled={bad} onClick={() => onFill(days, a, b)}>Fill</Button>
    </div>
  );
}
